import {useDispatch, useSelector} from "react-redux";
import {type Dispatch, type SetStateAction, useEffect, useMemo} from "react";
import {useLocation, useParams} from "react-router-dom";

import type {RootState} from "../../../store";
import {MessageType} from "../../../services/signalR/models/Messages";
import {useAuth} from "../../../services/auth/hooks/useAuth";
import {markAsRead} from "../../layout/messagesSlice";

interface ConstructionCalculationsLocationState {
  calculationId?: string | null;
};

const useMessagePageReloadHook = (
  loadPageData: (selectedCalculationId?: string | null) => Promise<void>,
  setLoading: Dispatch<SetStateAction<boolean>>,
): void => {
  const {messages} = useSelector((state: RootState) => state.signalRMessages);
  const dispatch = useDispatch();
  const eventsThatShouldTriggerDataRefresh = useMemo(
    () => [
      MessageType.CalculationCompleted,
      MessageType.ConstructionCalculationUpdated,
      MessageType.ConstructionAccepted,
      MessageType.ConstructionUpdated,
      MessageType.ConstructionUnaccepted,
      MessageType.ConstructionCalculationUnaccepted,
      MessageType.DrillholesCodeUpdated,
    ],
    [],
  );

  const {id} = useParams();
  const {user} = useAuth();
  const {state} = useLocation() as { state: ConstructionCalculationsLocationState}

  useEffect(() => {
    void (async () => {
      const relatedMessages = messages.filter(
        m =>
          m.data?.constructionId === id &&
          !m.isRead &&
          m.user === user.userName,
      );
      if (relatedMessages.length > 0) {
        const firstUnreadMessage = relatedMessages[0];
        dispatch(markAsRead(firstUnreadMessage.id));

        if (
          eventsThatShouldTriggerDataRefresh.some(
            e => e === firstUnreadMessage?.type,
          )
        ) {
          setLoading(true);
          await loadPageData(
            firstUnreadMessage.data?.calculationId ?? state?.calculationId,
          );
          setLoading(false);
        }
      }
    })();
  }, [
    dispatch,
    eventsThatShouldTriggerDataRefresh,
    id,
    loadPageData,
    messages,
    setLoading,
    state?.calculationId,
    user.userName,
  ]);
};

export default useMessagePageReloadHook;
